import { FC, useCallback, useEffect, useMemo } from 'react';
import { FormikErrors, FormikTouched, getIn, useFormikContext } from 'formik';
import dayjs from 'dayjs';
import { AddOutlined, DeleteOutline } from '@mui/icons-material';

import { CustomSelect, Picker, Text } from '../../../shared/uiComponents';
import { SelectDataProperties } from '../../../shared/uiComponents/Dropdown';
import { ORANGE, RED, TEXT } from '../../../shared/Helpers/colors';
import { ScheduleCancellationViewModel, CancellationStatus } from '../../../redux/API/ClientAPIHelpers/scheduleCancellation';
import { AdminTypes } from '../../../components/Menu';
import { WEEKDAYS } from './constants';
import { emptyTimeSlot, findAuthorizationPeriod, findBtDefaultSessionTypeId } from './helpers';
import { AuthorizationPeriodsBySessionType, ProviderOption, ScheduleBlockValues, ScheduleFormValues, SessionTypeOption, WeekdayCode } from './types';

interface ScheduleBlockProperties {
	authorizationPeriods: AuthorizationPeriodsBySessionType;
	cancellationsByProvider: Record<string, ScheduleCancellationViewModel[]>;
	canRemove: boolean;
	index: number;
	onRemove: () => void;
	providers: ProviderOption[];
	sessionTypesByProvider: Record<string, SessionTypeOption[]>;
}

const buildProviderOptions = (providers: ProviderOption[]): SelectDataProperties[] => {
	const bts = providers.filter((p) => p.roleSectionId === AdminTypes.bt);
	const bcbas = providers.filter((p) => p.roleSectionId !== AdminTypes.bt);
	const options: SelectDataProperties[] = [];

	if (bts.length) {
		options.push({ id: 'group-bt', name: 'Behavior Technicians', isDisabled: true });
		bts.forEach((p) => options.push({ id: p.id, name: p.label }));
	}

	if (bcbas.length) {
		options.push({ id: 'group-bcba', name: 'BCBAs', isDisabled: true });
		bcbas.forEach((p) => options.push({ id: p.id, name: p.label }));
	}

	return options;
};

const ScheduleBlock: FC<ScheduleBlockProperties> = ({
	index,
	providers,
	sessionTypesByProvider,
	authorizationPeriods,
	cancellationsByProvider,
	onRemove,
	canRemove,
}) => {
	const { values, setFieldValue, errors, touched, submitCount } = useFormikContext<ScheduleFormValues>();
	const block = values.schedules[index];

	const blockErrors = (errors.schedules?.[index] || {}) as FormikErrors<ScheduleBlockValues>;
	const blockTouched = (touched.schedules?.[index] || {}) as FormikTouched<ScheduleBlockValues>;
	const showError = (field: keyof ScheduleBlockValues): string =>
		blockTouched[field] && blockErrors[field] ? (blockErrors[field] as string) : '';
	const timeSlotsError = typeof blockErrors.timeSlots === 'string' && (blockTouched.timeSlots || submitCount > 0)
		? blockErrors.timeSlots
		: '';

	const sessionTypes = block.providerId ? sessionTypesByProvider[block.providerId] || [] : [];

	const overlappingCancellations = useMemo(() => {
		if (!block.providerId || !block.startDate || !block.endDate) return [];

		const cancellations = cancellationsByProvider[block.providerId] || [];

		return cancellations.filter((c) => {
			const cStart = c.startDate.slice(0, 10);
			const cEnd = c.endDate.slice(0, 10);

			return cStart <= block.endDate && cEnd >= block.startDate;
		});
	}, [block.providerId, block.startDate, block.endDate, cancellationsByProvider]);

	const setField = useCallback(
		(field: keyof ScheduleBlockValues, value: unknown) => setFieldValue(`schedules.${index}.${field}`, value),
		[setFieldValue, index],
	);

	const handleProviderChange = (providerId: string) => {
		// Session type (and its AI hint) depend on the provider, so changing providers invalidates
		// them: clear the stale hint and re-derive the new provider's default session type.
		setFieldValue(`schedules.${index}`, {
			...block,
			providerId,
			sessionTypeId: findBtDefaultSessionTypeId(providerId, providers, sessionTypesByProvider[providerId] || []),
			sessionTypeHint: undefined,
		});
	};

	useEffect(() => {
		if (!block.providerId || block.sessionTypeId || block.sessionTypeHint) return;

		const defaultType = findBtDefaultSessionTypeId(block.providerId, providers, sessionTypesByProvider[block.providerId] || []);

		if (defaultType) setField('sessionTypeId', defaultType);
	}, [block.providerId, block.sessionTypeId, block.sessionTypeHint, providers, sessionTypesByProvider, setField]);

	const handleSessionTypeChange = (sessionTypeId: string) => {
		const periods = authorizationPeriods[sessionTypeId] || [];
		const period = findAuthorizationPeriod(periods, block.startDate);
		const startDate = period && (!block.startDate || block.startDate < period.startDate)
			? period.startDate
			: block.startDate;
		const endDate = period && !block.endDate ? period.endDate : block.endDate;

		setFieldValue(`schedules.${index}`, { ...block, sessionTypeId, startDate, endDate });
	};

	const setTimeSlots = (timeSlots: ScheduleBlockValues['timeSlots']) => setField('timeSlots', timeSlots);
	const enableDay = (day: WeekdayCode) => {
		const reusableIndex = block.timeSlots.findIndex((slot) => slot.days.length === 0);
		if (reusableIndex >= 0) {
			setTimeSlots(block.timeSlots.map((slot, slotIndex) => slotIndex === reusableIndex ? { ...slot, days: [day] } : slot));
			return;
		}
		setTimeSlots([...block.timeSlots, { ...emptyTimeSlot(), days: [day] }]);
	};
	const disableDay = (day: WeekdayCode) => {
		const remaining = block.timeSlots.flatMap((slot) => {
			if (!slot.days.includes(day)) return [slot];
			if (slot.days.length > 1) return [{ ...slot, days: slot.days.filter((value) => value !== day) }];
			return [];
		});
		setTimeSlots(remaining.length ? remaining : [emptyTimeSlot()]);
	};
	const updateDayTime = (slotIndex: number, day: WeekdayCode, field: 'startTime' | 'endTime', value: string) => {
		const slot = block.timeSlots[slotIndex];
		if (slot.days.length === 1) {
			setFieldValue(`schedules.${index}.timeSlots.${slotIndex}.${field}`, value);
			return;
		}
		const separated = block.timeSlots.map((item, itemIndex) => itemIndex === slotIndex
			? { ...item, days: item.days.filter((itemDay) => itemDay !== day) }
			: item);
		setTimeSlots([...separated, { ...slot, ...emptyTimeSlot(), days: [day], startTime: slot.startTime, endTime: slot.endTime, [field]: value }]);
	};
	const removeDayTime = (slotIndex: number, day: WeekdayCode) => {
		const slot = block.timeSlots[slotIndex];
		const remaining = slot.days.length > 1
			? block.timeSlots.map((item, itemIndex) => itemIndex === slotIndex ? { ...item, days: item.days.filter((itemDay) => itemDay !== day) } : item)
			: block.timeSlots.filter((_, itemIndex) => itemIndex !== slotIndex);
		setTimeSlots(remaining.length ? remaining : [emptyTimeSlot()]);
	};


	return (
		<div className="scheduleBlock">
			<div className="scheduleBlockHeader">
				<Text title={`Schedule ${index + 1}`} type="pmd" fontWeight="semiBold" />
				{canRemove && (
					<DeleteOutline
						fontSize="small"
						style={{ color: RED.BASE, cursor: 'pointer' }}
						onClick={onRemove}
					/>
				)}
			</div>

			{overlappingCancellations.length > 0 && (
				<div className="cancellationWarningBanner">
					<Text
						title="Cancellation Conflict"
						type="pmd"
						fontWeight="semiBold"
						color={ORANGE.BASE}
					/>
					{overlappingCancellations.map((c) => {
						const start = dayjs(c.startDate).format('MMM D, YYYY');
						const end = dayjs(c.endDate).format('MMM D, YYYY');
						const statusLabel = c.status === CancellationStatus.Approved
							? 'approved'
							: c.status === CancellationStatus.Pending
								? 'pending approval'
								: 'applied';

						return (
							<Text
								key={c.id}
								title={`This provider has ${statusLabel} cancellation from ${start} to ${end}${c.reasonName ? ` (${c.reasonName})` : ''}. Sessions on those dates will appear as cancelled.`}
								type="psm"
								color={TEXT.TERTIARY}
							/>
						);
					})}
				</div>
			)}

			<div className="scheduleFieldRow">
				<Picker.CustomDate
					label="Start Date *"
					value={block.startDate ? dayjs(block.startDate) : null}
					onChange={(date) => setField('startDate', date ? date.format('YYYY-MM-DD') : '')}
					error={Boolean(showError('startDate'))}
					errorMessage={showError('startDate')}
					description="First session will be scheduled on or after this date"
				/>
				<Picker.CustomDate
					label="End Date *"
					value={block.endDate ? dayjs(block.endDate) : null}
					onChange={(date) => setField('endDate', date ? date.format('YYYY-MM-DD') : '')}
					error={Boolean(showError('endDate'))}
					errorMessage={showError('endDate')}
					description="Defaults to authorization expiration date"
				/>
			</div>

			<CustomSelect
				label="Assign Provider *"
				placeholder="Choose provider..."
				data={buildProviderOptions(providers)}
				value={block.providerId}
				setValue={(e) => handleProviderChange(e.target.value)}
				error={Boolean(showError('providerId'))}
				errorMessage={showError('providerId')}
				description="One provider per schedule. Session types below populate based on this provider's allocated sessions for this client."
			/>

			<CustomSelect
				label="Session Type *"
				data={sessionTypes.map((s) => ({ id: s.id, name: s.label }))}
				value={block.sessionTypeId}
				setValue={(e) => handleSessionTypeChange(e.target.value)}
				disabled={!block.providerId}
				error={Boolean(showError('sessionTypeId'))}
				errorMessage={showError('sessionTypeId')}
				description={
					block.providerId
						? 'Only session types this provider has allocated for this client appear here'
						: 'Select a provider first...'
				}
			/>

			<div className="scheduleDaysSection">
				<div className="scheduleSectionIntro">
					<Text title="Weekly schedule *" type="psm" fontWeight="medium" />
					<Text title="Select each day this schedule repeats. Add more than one time when a day has multiple sessions." type="pxs" color={TEXT.TERTIARY} />
				</div>
				<div className="frequencyToggle">
					<button type="button" className={`frequencyOption ${block.step === 1 ? 'active' : ''}`} onClick={() => setField('step', 1)}>Every week</button>
					<button type="button" className={`frequencyOption ${block.step === 2 ? 'active' : ''}`} onClick={() => setField('step', 2)}>Every other week</button>
				</div>
				<div className="dayToggles">
					{WEEKDAYS.map((day) => {
						const enabled = block.timeSlots.some((slot) => slot.days.includes(day.code));
						return (
							<button key={day.code} type="button" className={`dayToggle ${enabled ? 'active' : ''}`} aria-pressed={enabled} onClick={() => enabled ? disableDay(day.code) : enableDay(day.code)}>
								{day.short}
							</button>
						);
					})}
				</div>
				{WEEKDAYS.filter((day) => block.timeSlots.some((slot) => slot.days.includes(day.code))).map((day) => (
					<div className="scheduleDayCard" key={day.code}>
						<div className="scheduleDayHeader">
							<Text title={day.long} type="psm" fontWeight="semiBold" />
							<button type="button" className="scheduleDayRemove" onClick={() => disableDay(day.code)}>Remove day</button>
						</div>
						{block.timeSlots.map((slot, slotIndex) => {
							if (!slot.days.includes(day.code)) return null;
							const slotPath = `schedules.${index}.timeSlots.${slotIndex}`;
							const slotErrors = getIn(errors, slotPath) as Record<string, string> | undefined;
							const slotTouched = getIn(touched, slotPath) as Record<string, boolean> | undefined;
							const slotError = (field: 'startTime' | 'endTime') => slotTouched?.[field] && slotErrors?.[field] ? slotErrors[field] : '';
							return (
								<div className="scheduleDayTimeRow" key={`${slot.uid}-${day.code}`}>
									<Picker.CustomTime label="Start time *" value={slot.startTime ? dayjs(`2000-01-01T${slot.startTime}`) : null} onChange={(time) => updateDayTime(slotIndex, day.code, 'startTime', time ? time.format('HH:mm') : '')} error={Boolean(slotError('startTime'))} errorMessage={slotError('startTime')} />
									<Picker.CustomTime label="End time *" value={slot.endTime ? dayjs(`2000-01-01T${slot.endTime}`) : null} onChange={(time) => updateDayTime(slotIndex, day.code, 'endTime', time ? time.format('HH:mm') : '')} error={Boolean(slotError('endTime'))} errorMessage={slotError('endTime')} />
									<button type="button" className="scheduleTimeRemove" aria-label={`Remove ${day.long} time`} onClick={() => removeDayTime(slotIndex, day.code)}><DeleteOutline fontSize="small" /></button>
								</div>
							);
						})}
						<button type="button" className="scheduleAddTime" onClick={() => setTimeSlots([...block.timeSlots, { ...emptyTimeSlot(), days: [day.code] }])}>
							<AddOutlined fontSize="small" /> Add another time
						</button>
					</div>
				))}
				{!block.timeSlots.some((slot) => slot.days.length > 0) && <Text title="Select at least one day" type="pxs" color={RED.BASE} />}
				{Boolean(timeSlotsError) && <Text title={timeSlotsError} type="pxs" color={RED.BASE} />}
			</div>

			<Text
				title="Provider can clock in within this approved window"
				type="pxs"
				color={TEXT.TERTIARY}
				style={{ marginTop: '4px' }}
			/>

		</div>
	);
};

export default ScheduleBlock;
